
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// @components
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import CostFiles from '@/modules/InvoiceModule/components/CostFiles/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import InvoiceStatusRow from '@/modules/InvoiceModule/components/InvoiceStatusRow/index.vue'

// @types
import { InvoiceT } from '@/core/types/orders.types'

// @realisations
import { orders } from '@/core/realisations/orders.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import { OktellWaitListItem } from '@/core/types/oktell.types'
import { oktell } from '@/core/realisations/oktell.realisation'

const COLUMNS = [
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'state',
        name: 'Состояние',
    },
    {
        key: 'user',
        name: 'Кто проставил',
    },
    {
        key: 'clientType',
        name: 'Когда проставил',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'source',
        name: 'Источник создания',
    },
]

export default defineComponent({
    components: {
        NewButton,
        Loader,
        TheInput,
        Cheap,
        TheTextarea,
        TheButton,
        CostFiles,
        NewTable,
        NewModal,
        InvoiceStatusRow,
    },
    props: {
        invoiceId: {
            type: Number,
            required: true,
        },
        isDelivery: {
            type: Boolean,
            default: false,
        },
        hasParkingFiles: {
            type: Boolean,
            default: false,
        },
        hasRoadFiles: {
            type: Boolean,
            default: false,
        },
        row: {
            type: Object as PropType<any>,
            default: null,
        },
    },
    emits: ['cancel', 'payment', 'wait-list', 'invoice-loaded'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            invoice: null as InvoiceT | null,
            problems: [] as string[],
            sla_date: null as string | null,
        })

        const state_lo = reactive({
            is_loading: false,
            is_open: false,
            list: [] as OktellWaitListItem[],
        })

        const form = reactive({
            places: null as string | null,
            weight: null as string | null,
            volumeWeight: null as string | null,
            fullAddress: null as string | null,
            title: null as string | null,
            phone: null as string | null,
            additional_phone: null as string | null,
            fullName: null as string | null,
            direction: null as string | null,
            manager: null as string | null,
            delivery_date: null as string | null,
            company_name: null as string | null,
        })

        const fillInfo = (data: InvoiceT) => {
            if (!data) return

            state.invoice = data

            emit('invoice-loaded', { ...data })

            form.places = data.places.toString()
            form.weight = data.weight.toString()
            form.volumeWeight = data.volume_weight.toString()
            form.fullAddress = data.receiver.full_address
            form.title = data.receiver.title
            form.phone = data.receiver.phone
            form.additional_phone = data.receiver.additional_phone
            form.fullName = data.receiver.full_name
            form.direction = data.direction
            form.delivery_date = data.delivery_date
            form.company_name = data.company_name
            form.manager = data.manager ? data.manager.email : null
        }

        const loadInvoice = async () => {
            try {
                state.is_loading = true

                const response = await orders.getInvoice(props.invoiceId)
                fillInfo(response.data)

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных накладной произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadInvoiceProblems = async () => {
            try {
                state.is_loading = true

                const response = await orders.getInvoiceProblems(props.invoiceId)
                state.problems = response.data.problems
                state.sla_date = response.data.slaDate

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении проблем по накладной произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadWaitList = async () => {
            if (!state.invoice) return

            try {
                state.is_loading = true

                const params = {
                    clientIds: [state.invoice.id, state.invoice.order_id],
                    isCompleted: 1,
                }
                const response = await oktell.getWaitList(params)
                state_lo.list = response.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке произошла ошибка',
                })
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const toggleLo = () => (state_lo.is_open = !state_lo.is_open)

        const emitCancel = () => {
            emit('cancel')
        }

        const emitPayment = (type: number) => emit('payment', type, props.invoiceId)

        const emitWaitList = () => {
            emit('wait-list', { ...props.row })
        }

        //

        onBeforeMount(async () => {
            await loadInvoice()
            loadInvoiceProblems()

            if (props.isDelivery) loadWaitList()
        })

        //

        return {
            state,
            state_lo,
            form,
            //
            loadInvoice,
            emitCancel,
            emitPayment,
            emitWaitList,
            toggleLo,
            //
            PHONE_NUMBER_MASK,
            COLUMNS,
        }
    },
})
